/**
 * Well linkage + series helpers for Voronoi MB Production UI.
 * Links come from simulate() grids[].links — same rules as handoff wellData.js:
 * linked iff !blocked && w > 0 (LOS-visible Voronoi overlap share).
 */
import type { MaterialBalanceResult, MbGridLink, MbWellSeriesRow } from '@/types/materialBalance';

export function linkedInjs(res: MaterialBalanceResult | null | undefined, prodId: string): string[] {
  const set = new Set<string>();
  for (const g of res?.grids || []) {
    for (const l of g.links || []) {
      if (l.prod === prodId && !l.blocked && l.w > 0) set.add(l.inj);
    }
  }
  return [...set].sort();
}

export function linkedProds(res: MaterialBalanceResult | null | undefined, injId: string): string[] {
  const set = new Set<string>();
  for (const g of res?.grids || []) {
    for (const l of g.links || []) {
      if (l.inj === injId && !l.blocked && l.w > 0) set.add(l.prod);
    }
  }
  return [...set].sort();
}

export function prodSeries(res: MaterialBalanceResult | null | undefined, prodId: string) {
  const rows = (res?.series?.[prodId] || []).filter((r) => r.kind === 'prod');
  return {
    oil: rows.map((r) => ({ year: r.year, value: r.oil ?? 0 })),
    liq: rows.map((r) => ({ year: r.year, value: r.liq ?? 0 })),
    /** Engine stores fw already in % (0–100). */
    waterCut: rows.map((r) => ({ year: r.year, value: r.fw ?? 0 })),
    recvInj: rows.map((r) => ({
      year: r.year,
      value:
        r.w ??
        Object.values(r.wi_inj || r.recv_from || {}).reduce((s, v) => s + Number(v || 0), 0),
    })),
    last: rows.at(-1) as MbWellSeriesRow | undefined,
  };
}

export function injSeries(
  res: MaterialBalanceResult | null | undefined,
  injId: string,
  injWells: Record<string, Record<string, number>> = {},
) {
  const custom = injWells[injId] || {};
  const rows = res?.series?.[injId] || [];
  return rows.map((r) => ({
    year: r.year,
    value: custom[r.year] ?? custom[String(r.year)] ?? r.inj ?? 0,
  }));
}

const OIL_COLORS = ['#3dcc7a', '#56e6a8', '#2eb85c', '#7dffb2', '#1fa463', '#a8f0c8'];
const LIQ_COLORS = ['#7ab0ff', '#9ec5ff', '#5a9fd4', '#b8d4ff', '#4a8ec4', '#cfe4ff'];

export function linkedProdRateSeries(
  res: MaterialBalanceResult | null | undefined,
  injId: string,
): { id: string; color: string; dash?: string; pts: { year: number; value: number }[] }[] {
  const prods = linkedProds(res, injId);
  const series: {
    id: string;
    color: string;
    dash?: string;
    pts: { year: number; value: number }[];
  }[] = [];
  prods.forEach((pid, i) => {
    const rows = (res?.series?.[pid] || []).filter((r) => r.kind === 'prod');
    series.push({
      id: `${pid} oil`,
      pts: rows.map((r) => ({ year: r.year, value: r.oil ?? 0 })),
      color: OIL_COLORS[i % OIL_COLORS.length],
    });
    series.push({
      id: `${pid} liq`,
      pts: rows.map((r) => ({ year: r.year, value: r.liq ?? 0 })),
      color: LIQ_COLORS[i % LIQ_COLORS.length],
      dash: '5 4',
    });
  });
  return series;
}

/** Engine well id from scenario well name (matches voronoi_mb_service.build_wells_from_db). */
export function mbEngineWellId(wellName: string, fallbackId: string): string {
  return (wellName || fallbackId).replace(/\//g, '_').replace(/ /g, '_');
}

export function collectLinksForYear(
  res: MaterialBalanceResult | null | undefined,
  year?: number | null,
): MbGridLink[] {
  if (!res?.grids?.length) return [];
  const g =
    year != null
      ? res.grids.find((x) => x.year === year) || res.grids[res.grids.length - 1]
      : res.grids[res.grids.length - 1];
  return g?.links || [];
}
